import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, ChevronDown, ChevronRight, SplitSquareHorizontal, Star, Upload, Image as ImageIcon, Percent, Ruler, UtensilsCrossed, Package, Plus, Trash2, Search, FlaskConical, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SECTIONS = ["basic", "display", "modifiers"];
const COLOR_OPTIONS = [
  { name: "None", value: "" }, { name: "Blue", value: "#3B82F6" },
  { name: "Brown", value: "#92400E" }, { name: "Purple", value: "#7C3AED" },
  { name: "Fuchsia", value: "#D946EF" }, { name: "Green", value: "#22C55E" },
  { name: "Teal", value: "#14B8A6" }, { name: "Red", value: "#EF4444" },
  { name: "Orange", value: "#F97316" }, { name: "Amber", value: "#F59E0B" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SortableModGroupRow({ group, idx, form, toggleModGroup, activeSizes, updateOverride, updateModOverride }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.modifier_group_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : "auto" };
  const gid = group.modifier_group_id;
  const overrides = form.modifier_group_overrides?.[gid] || {};
  const modOverrides = overrides.modifier_overrides || {};
  const [expanded, setExpanded] = useState(false);
  const hasSizes = activeSizes.length > 0;

  const gMin = overrides.min_select ?? group.min_select ?? 0;
  const gMax = overrides.max_select ?? group.max_select ?? 0;
  const gFree = overrides.free_amount ?? group.free_amount ?? 0;

  const updateSizePriceOv = (modId, sizeId, value) => {
    const mOv = modOverrides[modId] || {};
    const spo = { ...(mOv.size_price_overrides || {}) };
    if (value === undefined || value === "") { delete spo[sizeId]; } else { spo[sizeId] = value; }
    updateModOverride(gid, modId, "size_price_overrides", Object.keys(spo).length > 0 ? spo : undefined);
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className={`border rounded-lg overflow-hidden border-[var(--brand-primary)] bg-blue-50/30 ${isDragging ? "shadow-lg ring-2 ring-blue-300" : ""}`}>
      <div className="flex items-center gap-2 px-4 py-3">
        <button {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 text-slate-400 hover:text-slate-600 touch-none flex-shrink-0" data-testid={`drag-modgroup-${gid}`}>
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="text-xs font-bold text-[var(--brand-primary)] bg-blue-100 px-1.5 py-0.5 rounded">{idx + 1}</span>
        <Checkbox checked={true} onCheckedChange={() => toggleModGroup(gid)} data-testid={`assign-group-${gid}`} />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-900">{group.name}</span>
          <span className="text-xs text-slate-400 ml-2">(Min: {gMin}, Max: {gMax}, Free: {gFree})</span>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-blue-600 hover:underline font-medium px-2 py-1" data-testid={`toggle-overrides-${gid}`}>
          {expanded ? "Hide" : "Overrides"}
        </button>
        <Badge variant="outline" className="text-xs text-[var(--brand-primary)]">#{idx + 1}</Badge>
      </div>

      {/* Group-level overrides */}
      {expanded && (
        <div className="border-t border-blue-200 bg-blue-50/50 px-4 py-3">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Group Overrides (for this product only)</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div>
              <Label className="text-[10px] text-slate-500">Min Select</Label>
              <Input type="number" min="0" className="h-7 text-xs mt-0.5"
                value={overrides.min_select ?? ""}
                placeholder={String(group.min_select ?? 0)}
                onChange={(e) => updateOverride(gid, "min_select", e.target.value === "" ? undefined : parseInt(e.target.value))}
                data-testid={`override-min-${gid}`} />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Max Select</Label>
              <Input type="number" min="0" className="h-7 text-xs mt-0.5"
                value={overrides.max_select ?? ""}
                placeholder={String(group.max_select ?? 0)}
                onChange={(e) => updateOverride(gid, "max_select", e.target.value === "" ? undefined : parseInt(e.target.value))}
                data-testid={`override-max-${gid}`} />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Free Amount</Label>
              <Input type="number" min="0" className="h-7 text-xs mt-0.5"
                value={overrides.free_amount ?? ""}
                placeholder={String(group.free_amount ?? 0)}
                onChange={(e) => updateOverride(gid, "free_amount", e.target.value === "" ? undefined : parseInt(e.target.value))}
                data-testid={`override-free-${gid}`} />
            </div>
          </div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Per-Modifier Overrides</p>
        </div>
      )}

      {/* Modifiers list */}
      {group.modifiers?.length > 0 && (
        <div className="border-t border-slate-200 bg-white overflow-x-auto text-sm">
          {expanded && (
            <div className="grid gap-1 px-4 py-1.5 bg-slate-50 border-b border-slate-200"
              style={{ gridTemplateColumns: `1fr 48px 48px ${hasSizes ? activeSizes.map(() => "55px").join(" ") : "55px"} 48px 48px` }}>
              <span className="text-[9px] font-bold text-slate-400 uppercase">Modifier</span>
              <span className="text-[9px] font-bold text-slate-400 uppercase text-center">Def</span>
              <span className="text-[9px] font-bold text-slate-400 uppercase text-center">Qty</span>
              {hasSizes ? activeSizes.map(s => (
                <span key={s.size_id} className="text-[9px] font-bold text-slate-400 uppercase text-center" title={s.name}>${s.short_name}</span>
              )) : <span className="text-[9px] font-bold text-slate-400 uppercase text-center">Price</span>}
              <span className="text-[9px] font-bold text-slate-400 uppercase text-center">Free</span>
              <span className="text-[9px] font-bold text-slate-400 uppercase text-center">Max</span>
            </div>
          )}
          {group.modifiers.map((mod) => {
            const mOv = modOverrides[mod.modifier_id] || {};
            const spo = mOv.size_price_overrides || {};
            return (
              <div key={mod.modifier_id} className={`border-b border-slate-50 ${mOv.is_default ? "bg-amber-50/40" : ""}`}>
                {expanded ? (
                  <div className="grid gap-1 px-4 py-1.5 items-center"
                    style={{ gridTemplateColumns: `1fr 48px 48px ${hasSizes ? activeSizes.map(() => "55px").join(" ") : "55px"} 48px 48px` }}>
                    <div className="flex items-center gap-1 min-w-0">
                      <span className="text-xs text-slate-700 truncate">{mod.name}</span>
                      <span className="text-[10px] font-mono text-slate-400 shrink-0">${mod.price?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-center">
                      <Checkbox checked={mOv.is_default || false}
                        onCheckedChange={(v) => updateModOverride(gid, mod.modifier_id, "is_default", v)}
                        data-testid={`mod-default-${mod.modifier_id}`} />
                    </div>
                    <Input type="number" min="0" className="h-6 text-[10px] w-11 text-center px-0.5"
                      value={mOv.default_quantity ?? ""} placeholder="1" disabled={!mOv.is_default}
                      onChange={(e) => updateModOverride(gid, mod.modifier_id, "default_quantity", e.target.value === "" ? undefined : parseInt(e.target.value))} />
                    {hasSizes ? activeSizes.map(s => {
                      const existingOffset = Array.isArray(mod.size_offsets) ? mod.size_offsets.find(so => so.size_id === s.size_id)?.price : null;
                      return (
                        <Input key={s.size_id} type="number" step="0.01" min="0" className="h-6 text-[10px] w-12 text-center px-0.5"
                          value={spo[s.size_id] ?? ""} placeholder={existingOffset != null ? existingOffset.toFixed(2) : mod.price?.toFixed(2)}
                          onChange={(e) => updateSizePriceOv(mod.modifier_id, s.size_id, e.target.value === "" ? undefined : parseFloat(e.target.value))} />
                      );
                    }) : (
                      <Input type="number" step="0.01" min="0" className="h-6 text-[10px] w-12 text-center px-0.5"
                        value={mOv.price_override ?? ""} placeholder={mod.price?.toFixed(2)}
                        onChange={(e) => updateModOverride(gid, mod.modifier_id, "price_override", e.target.value === "" ? undefined : parseFloat(e.target.value))} />
                    )}
                    <Input type="number" min="0" className="h-6 text-[10px] w-11 text-center px-0.5"
                      value={mOv.free_quantity ?? ""} placeholder="0"
                      onChange={(e) => updateModOverride(gid, mod.modifier_id, "free_quantity", e.target.value === "" ? undefined : parseInt(e.target.value))} />
                    <Input type="number" min="0" className="h-6 text-[10px] w-11 text-center px-0.5"
                      value={mOv.max_quantity ?? ""} placeholder="-"
                      onChange={(e) => updateModOverride(gid, mod.modifier_id, "max_quantity", e.target.value === "" ? undefined : parseInt(e.target.value))} />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-4 py-1.5">
                    <span className="text-xs text-slate-700 flex-1 truncate">{mod.name}</span>
                    {mOv.is_default && <Badge className="text-[9px] bg-amber-100 text-amber-700">Default{mOv.default_quantity > 1 ? ` x${mOv.default_quantity}` : ""}</Badge>}
                    {mOv.max_quantity != null && <Badge variant="outline" className="text-[9px]">Max:{mOv.max_quantity}</Badge>}
                    {(mOv.price_override != null || Object.keys(spo).length > 0) && <span className="text-[10px] font-mono text-blue-600">{Object.keys(spo).length > 0 ? `${Object.keys(spo).length} sizes` : `$${mOv.price_override.toFixed(2)}`}</span>}
                    {mOv.free_quantity != null && <Badge variant="outline" className="text-[9px]">Free:{mOv.free_quantity}</Badge>}
                    {!mOv.is_default && mOv.price_override == null && Object.keys(spo).length === 0 && <span className="text-xs font-mono text-slate-500">{mod.price > 0 ? `$${mod.price.toFixed(2)}` : "Free"}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = productId === "new";
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState({ basic: true, display: false, dining: false, sizes: false, taxes: false, modifiers: false, combo: false, recipe: false });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [allModGroups, setAllModGroups] = useState([]);
  const [allTaxRates, setAllTaxRates] = useState([]);
  const [allSizes, setAllSizes] = useState([]);
  const [allSizeGroups, setAllSizeGroups] = useState([]);
  const [allDiningTypes, setAllDiningTypes] = useState([]);

  const [form, setForm] = useState({
    name: "", description: "", product_class: "", barcode: "",
    price_embedded: false, sku: "", cost_price: "", price: "",
    allow_price_override: false, eligible_for_discounts: true,
    is_active: true, sort_order: 0, category_id: "", subcategory_id: "",
    image_url: "", color_code: "", display_conversational: true,
    disable_modifier_popup: false, printers: [],
    modifier_group_ids: [], default_modifier_ids: [],
    tax_ids: [], size_group_id: "", size_prices: [],
    dining_type_ids: [], modifier_size_availability: {},
    modifier_group_overrides: {},
    size_included_items: {},
    is_combo: false, combo_slots: [],
    recipe_ingredients: [], allow_negative_stock: false,
    loyalty_bonus_points: 0,
  });

  const [allIngredients, setAllIngredients] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [catRes, scRes, mgRes, trRes, sgRes, dtRes, ingRes] = await Promise.all([
        api.get("/categories"),
        api.get("/subcategories"),
        api.get("/modifier-groups"),
        api.get("/tax-rates"),
        api.get("/size-groups"),
        api.get("/dining-types"),
        api.get("/ingredients?active_only=true"),
      ]);
      setCategories(catRes.data);
      setSubcategories(scRes.data);
      setAllModGroups(mgRes.data);
      setAllTaxRates(trRes.data);
      setAllSizeGroups(sgRes.data);
      setAllDiningTypes(dtRes.data);
      setAllIngredients(ingRes.data);
      // Flatten all sizes
      const allSz = sgRes.data.flatMap((g) => g.sizes || []);
      setAllSizes(allSz);

      if (!isNew) {
        const pRes = await api.get(`/products/${productId}`);
        const p = pRes.data;
        setForm({
          name: p.name || "", description: p.description || "",
          product_class: p.product_class || "", barcode: p.barcode || "",
          price_embedded: p.price_embedded || false, sku: p.sku || "",
          cost_price: String(p.cost_price || ""), price: String(p.price || ""),
          allow_price_override: p.allow_price_override || false,
          eligible_for_discounts: p.eligible_for_discounts !== false,
          is_active: p.is_active !== false, sort_order: p.sort_order || 0,
          category_id: p.category_id || "", subcategory_id: p.subcategory_id || "",
          image_url: p.image_url || "", color_code: p.color_code || "",
          display_conversational: p.display_conversational !== false,
          disable_modifier_popup: p.disable_modifier_popup || false,
          printers: p.printers || [],
          modifier_group_ids: p.modifier_group_ids || [],
          default_modifier_ids: p.default_modifier_ids || [],
          tax_ids: p.tax_ids || [],
          size_group_id: p.size_group_id || "",
          size_prices: p.size_prices || [],
          dining_type_ids: p.dining_type_ids || [],
          modifier_size_availability: p.modifier_size_availability || {},
          modifier_group_overrides: p.modifier_group_overrides || {},
          size_included_items: p.size_included_items || {},
          is_combo: p.is_combo || false,
          combo_slots: p.combo_slots || [],
          recipe_ingredients: p.recipe_ingredients || [],
          allow_negative_stock: p.allow_negative_stock || false,
          loyalty_bonus_points: p.loyalty_bonus_points || 0,
        });
      } else {
        // Pre-fill category/subcategory from URL params for new products
        const catParam = searchParams.get("cat");
        const subcatParam = searchParams.get("subcat");
        if (catParam) update("category_id", catParam);
        if (subcatParam) update("subcategory_id", subcatParam);
      }
    } catch (err) {
      toast.error("Failed to load product data");
    } finally { setLoading(false); }
  }, [productId, isNew]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleSection = (s) => setExpanded((prev) => ({ ...prev, [s]: !prev[s] }));

  const toggleModGroup = (groupId) => {
    setForm((prev) => {
      const ids = prev.modifier_group_ids.includes(groupId)
        ? prev.modifier_group_ids.filter((x) => x !== groupId)
        : [...prev.modifier_group_ids, groupId];
      // Remove default modifiers from unassigned groups
      const removedGroup = !ids.includes(groupId);
      let defaults = prev.default_modifier_ids;
      if (removedGroup) {
        const group = allModGroups.find((g) => g.modifier_group_id === groupId);
        const modIds = (group?.modifiers || []).map((m) => m.modifier_id);
        defaults = defaults.filter((d) => !modIds.includes(d));
      }
      return { ...prev, modifier_group_ids: ids, default_modifier_ids: defaults };
    });
  };

  const toggleDefaultMod = (modId) => {
    setForm((prev) => ({
      ...prev,
      default_modifier_ids: prev.default_modifier_ids.includes(modId)
        ? prev.default_modifier_ids.filter((x) => x !== modId)
        : [...prev.default_modifier_ids, modId],
    }));
  };

  const updateGroupOverride = (groupId, field, value) => {
    setForm((prev) => {
      const overrides = { ...(prev.modifier_group_overrides || {}) };
      const groupOv = { ...(overrides[groupId] || {}) };
      if (value === undefined || value === "") {
        delete groupOv[field];
      } else {
        groupOv[field] = value;
      }
      overrides[groupId] = groupOv;
      return { ...prev, modifier_group_overrides: overrides };
    });
  };

  const updateModifierOverride = (groupId, modId, field, value) => {
    setForm((prev) => {
      const overrides = { ...(prev.modifier_group_overrides || {}) };
      const groupOv = { ...(overrides[groupId] || {}) };
      const modOverrides = { ...(groupOv.modifier_overrides || {}) };
      const modOv = { ...(modOverrides[modId] || {}) };
      if (value === undefined || value === "" || value === false) {
        delete modOv[field];
        // If clearing is_default, also clear default_quantity
        if (field === "is_default") {
          delete modOv["default_quantity"];
        }
      } else {
        modOv[field] = value;
      }
      // Clean up empty override objects
      if (Object.keys(modOv).length === 0) {
        delete modOverrides[modId];
      } else {
        modOverrides[modId] = modOv;
      }
      groupOv.modifier_overrides = modOverrides;
      overrides[groupId] = groupOv;
      return { ...prev, modifier_group_overrides: overrides };
    });
  };


  const toggleModSizeAvail = (modId, sizeId) => {
    setForm((prev) => {
      const avail = { ...(prev.modifier_size_availability || {}) };
      const current = avail[modId] || null; // null = all sizes
      if (current === null) {
        // First restriction: start with all sizes EXCEPT this one
        const allSizeIds = activeSizes.map((s) => s.size_id);
        avail[modId] = allSizeIds.filter((id) => id !== sizeId);
      } else if (current.includes(sizeId)) {
        // Remove this size
        const updated = current.filter((id) => id !== sizeId);
        if (updated.length === 0) {
          // If no sizes left, remove entry (effectively unavailable for all - but that's odd, so restore all)
          delete avail[modId];
        } else {
          avail[modId] = updated;
        }
      } else {
        // Add this size back
        const updated = [...current, sizeId];
        // If all sizes are now selected, remove the entry (means available for all)
        if (updated.length >= activeSizes.length) {
          delete avail[modId];
        } else {
          avail[modId] = updated;
        }
      }
      return { ...prev, modifier_size_availability: avail };
    });
  };

  const isModAvailForSize = (modId, sizeId) => {
    const avail = form.modifier_size_availability || {};
    if (!avail[modId]) return true; // No restriction = available for all
    return avail[modId].includes(sizeId);
  };

  const toggleTaxId = (taxId) => {
    setForm((prev) => ({
      ...prev,
      tax_ids: prev.tax_ids.includes(taxId) ? prev.tax_ids.filter((x) => x !== taxId) : [...prev.tax_ids, taxId],
    }));
  };

  const pdpSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleModGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = form.modifier_group_ids;
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    update("modifier_group_ids", arrayMove(ids, oldIdx, newIdx));
  };

  const toggleDiningType = (dtId) => {
    setForm((prev) => ({
      ...prev,
      dining_type_ids: prev.dining_type_ids.includes(dtId) ? prev.dining_type_ids.filter((x) => x !== dtId) : [...prev.dining_type_ids, dtId],
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = `${BACKEND_URL}/api/files/${res.data.storage_path}`;
      update("image_url", imageUrl);
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || "Unknown error"));
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        cost_price: parseFloat(form.cost_price) || 0,
        sort_order: parseInt(form.sort_order) || 0,
        stock_quantity: 0,
        is_combo: form.is_combo,
        combo_slots: form.combo_slots,
        recipe_ingredients: form.recipe_ingredients,
        allow_negative_stock: form.allow_negative_stock,
        loyalty_bonus_points: parseInt(form.loyalty_bonus_points) || 0,
      };
      if (isNew) {
        const res = await api.post("/products", payload);
        toast.success("Product created");
        navigate(`/dashboard/products/${res.data.product_id}`, { replace: true });
      } else {
        await api.put(`/products/${productId}`, payload);
        toast.success("Product saved");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const [allProducts, setAllProducts] = useState([]);
  const [comboSearchQuery, setComboSearchQuery] = useState("");

  // Fetch all products for combo slot eligible picker
  useEffect(() => {
    if (form.is_combo && allProducts.length === 0) {
      api.get("/products?active_only=true").then((res) => setAllProducts(res.data)).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.is_combo]);

  const addComboSlot = () => {
    const newSlot = {
      slot_id: `slot_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      slot_name: `Choice ${form.combo_slots.length + 1}`,
      quantity: 1,
      eligible_product_ids: [],
    };
    update("combo_slots", [...form.combo_slots, newSlot]);
  };

  const updateComboSlot = (slotId, key, value) => {
    update("combo_slots", form.combo_slots.map((s) => s.slot_id === slotId ? { ...s, [key]: value } : s));
  };

  const removeComboSlot = (slotId) => {
    update("combo_slots", form.combo_slots.filter((s) => s.slot_id !== slotId));
  };

  const toggleSlotProduct = (slotId, productId) => {
    update("combo_slots", form.combo_slots.map((s) => {
      if (s.slot_id !== slotId) return s;
      const ids = s.eligible_product_ids.includes(productId)
        ? s.eligible_product_ids.filter((id) => id !== productId)
        : [...s.eligible_product_ids, productId];
      return { ...s, eligible_product_ids: ids };
    }));
  };

  const filteredSubcats = subcategories.filter((sc) => sc.category_id === form.category_id);
  const getCatName = (id) => categories.find((c) => c.category_id === id)?.name || "";
  const getSubcatName = (id) => subcategories.find((s) => s.subcategory_id === id)?.name || "";

  // Get active sizes for the selected size group
  const selectedSizeGroup = allSizeGroups.find((g) => g.size_group_id === form.size_group_id);
  const activeSizes = (selectedSizeGroup?.sizes || []).filter((s) => s.is_active);

  // Handle modifier size offset changes (saves to modifier directly)
  const handleModSizeOffset = async (modifierId, groupId, sizeId, value) => {
    // Update local state immediately for responsiveness
    const groupIdx = allModGroups.findIndex((g) => g.modifier_group_id === groupId);
    if (groupIdx === -1) return;
    const newGroups = [...allModGroups];
    const modIdx = newGroups[groupIdx].modifiers.findIndex((m) => m.modifier_id === modifierId);
    if (modIdx === -1) return;
    const mod = { ...newGroups[groupIdx].modifiers[modIdx] };
    const offsets = [...(mod.size_offsets || [])].filter((so) => so.size_id !== sizeId);
    if (value && parseFloat(value) >= 0) offsets.push({ size_id: sizeId, price: parseFloat(value) });
    mod.size_offsets = offsets;
    newGroups[groupIdx] = { ...newGroups[groupIdx], modifiers: [...newGroups[groupIdx].modifiers] };
    newGroups[groupIdx].modifiers[modIdx] = mod;
    setAllModGroups(newGroups);

    // Debounced save to backend
    clearTimeout(window._modSaveTimer);
    window._modSaveTimer = setTimeout(async () => {
      try {
        await api.put(`/modifiers/${modifierId}`, { ...mod, modifier_group_id: groupId });
      } catch { /* silent - will sync on next load */ }
    }, 600);
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="product-detail-page">
      {/* Top Bar */}
      <div className="bg-[var(--brand-primary)] text-white px-4 py-3 flex items-center justify-between" data-testid="product-detail-header">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => navigate("/dashboard/products")} data-testid="back-to-products-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-sm opacity-80 truncate">
            Products {getCatName(form.category_id) && `> ${getCatName(form.category_id)}`} {getSubcatName(form.subcategory_id) && `> ${getSubcatName(form.subcategory_id)}`} &gt; <span className="font-semibold opacity-100">{form.name || "New Product"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="text-white hover:bg-white/20" onClick={() => navigate("/dashboard/products")} data-testid="back-btn">Back to Products</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-[var(--brand-primary)] hover:bg-white/90 font-semibold" data-testid="save-product-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="flex max-w-7xl mx-auto">
        {/* Left Sidebar - Section Navigation */}
        <div className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-52px)] p-4 flex-shrink-0" data-testid="product-sections-nav">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Select Desired Settings</p>
          {[
            { id: "basic", label: "Basic Product Details", sub: ["Name", "Description", "Product Class", "Barcode", "SKU", "Cost", "Price", "Active"] },
            { id: "display", label: "Display/Print Options", sub: ["Main Category", "Image", "Color Code", "Printers"] },
            { id: "dining", label: "Dining Types", sub: form.dining_type_ids.length > 0 ? [`${form.dining_type_ids.length} types selected`] : ["All types"] },
            { id: "sizes", label: "Sizes & Pricing", badge: form.size_prices.length > 0 ? form.size_prices.length : null },
            { id: "taxes", label: "Tax Options", sub: form.tax_ids.length > 0 ? [`${form.tax_ids.length} taxes applied`] : ["No taxes set"] },
            { id: "modifiers", label: "Modifiers", badge: form.modifier_group_ids.length > 0 ? "M" : null },
            { id: "combo", label: "Combo Meal", badge: form.is_combo ? "C" : null },
            { id: "recipe", label: "Recipe / Ingredients", badge: form.recipe_ingredients.length > 0 ? form.recipe_ingredients.length : null },
          ].map((section) => (
            <div key={section.id} className="mb-2">
              <button
                onClick={() => toggleSection(section.id)}
                className={`flex items-center gap-2 w-full text-left py-1.5 px-2 rounded text-sm font-medium ${expanded[section.id] ? "text-[var(--brand-primary)] bg-blue-50" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid={`section-toggle-${section.id}`}
              >
                {expanded[section.id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                <Checkbox checked={expanded[section.id]} className="border-slate-300" />
                <span>{section.label}</span>
                {section.badge && <Badge className="ml-auto text-xs bg-[var(--brand-primary)]">{section.badge}</Badge>}
              </button>
              {expanded[section.id] && section.sub && (
                <div className="pl-10 space-y-0.5 mt-1">
                  {section.sub.map((s) => (
                    <p key={s} className="text-xs text-[var(--brand-primary)]">{s}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 space-y-6" data-testid="product-detail-content">
          {/* === BASIC PRODUCT DETAILS === */}
          {expanded.basic && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="basic-details-section">
              <div className="border-b border-slate-200 px-6 py-3">
                <h2 className="font-heading text-base font-semibold text-slate-900">Basic Product Details</h2>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Name *</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="product-name-input" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <Label className="text-sm text-slate-600 text-right pt-2">Description</Label>
                  <textarea value={form.description} onChange={(e) => update("description", e.target.value)} className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="product-desc-input" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Product Class</Label>
                  <Input value={form.product_class} onChange={(e) => update("product_class", e.target.value)} data-testid="product-class-input" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Barcode</Label>
                  <Input value={form.barcode} onChange={(e) => update("barcode", e.target.value)} data-testid="product-barcode-input" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Price embedded</Label>
                  <Checkbox checked={form.price_embedded} onCheckedChange={(v) => update("price_embedded", v)} />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">SKU</Label>
                  <Input value={form.sku} onChange={(e) => update("sku", e.target.value)} data-testid="product-sku-input" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Cost</Label>
                  <Input type="number" step="0.01" value={form.cost_price} onChange={(e) => update("cost_price", e.target.value)} className="max-w-xs" data-testid="product-cost-input" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Price *</Label>
                  <Input type="number" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} className="max-w-xs" data-testid="product-price-input" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Allow price override</Label>
                  <Checkbox checked={form.allow_price_override} onCheckedChange={(v) => update("allow_price_override", v)} />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Eligible for Discounts</Label>
                  <Checkbox checked={form.eligible_for_discounts} onCheckedChange={(v) => update("eligible_for_discounts", v)} />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Loyalty Bonus Points</Label>
                  <div>
                    <Input type="number" min="0" value={form.loyalty_bonus_points} onChange={(e) => update("loyalty_bonus_points", e.target.value)} className="max-w-xs" data-testid="product-loyalty-bonus" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Extra points earned when this product is purchased (in addition to $/point rate)</p>
                  </div>
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Active</Label>
                  <Checkbox checked={form.is_active} onCheckedChange={(v) => update("is_active", v)} data-testid="product-active-check" />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Sort Order</Label>
                  <Input type="number" value={form.sort_order} onChange={(e) => update("sort_order", e.target.value)} className="max-w-xs" />
                </div>
              </div>
            </div>
          )}

          {/* === DISPLAY/PRINT OPTIONS === */}
          {expanded.display && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="display-section">
              <div className="border-b border-slate-200 px-6 py-3">
                <h2 className="font-heading text-base font-semibold text-slate-900">Display/Print Options</h2>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Main Category</Label>
                  <Select value={form.category_id} onValueChange={(v) => { update("category_id", v); update("subcategory_id", ""); }}>
                    <SelectTrigger className="max-w-sm" data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {filteredSubcats.length > 0 && (
                  <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                    <Label className="text-sm text-slate-600 text-right">Subcategory</Label>
                    <Select value={form.subcategory_id} onValueChange={(v) => update("subcategory_id", v)}>
                      <SelectTrigger className="max-w-sm"><SelectValue placeholder="Select subcategory" /></SelectTrigger>
                      <SelectContent>{filteredSubcats.map((s) => <SelectItem key={s.subcategory_id} value={s.subcategory_id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Display in Ordering</Label>
                  <Checkbox checked={form.display_conversational} onCheckedChange={(v) => update("display_conversational", v)} />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <Label className="text-sm text-slate-600 text-right pt-2">Image</Label>
                  <div className="space-y-3">
                    {/* Upload Button */}
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="product-image-upload" />
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--brand-primary)] text-sm font-medium text-[var(--brand-primary)] hover:bg-blue-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                          <Upload className="h-4 w-4" />
                          {uploading ? "Uploading..." : "Browse & Upload"}
                        </div>
                      </label>
                      <span className="text-xs text-slate-400 self-center">or</span>
                      <span className="text-xs text-slate-400 self-center font-medium">Use URL below</span>
                    </div>
                    {/* URL Input */}
                    <Input value={form.image_url} onChange={(e) => update("image_url", e.target.value)} placeholder="https://..." className="max-w-lg" data-testid="product-image-input" />
                    {/* Preview */}
                    {form.image_url && (
                      <div className="border border-slate-200 rounded-lg p-2 inline-block bg-white">
                        <img
                          src={form.image_url}
                          alt="Product preview"
                          className="h-24 w-24 object-cover rounded"
                          onError={(e) => { e.target.style.display = "none"; }}
                          data-testid="product-image-preview"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-[180px_1fr] items-start gap-4">
                  <Label className="text-sm text-slate-600 text-right pt-2">Color Code</Label>
                  <div className="grid grid-cols-5 gap-2 max-w-sm">
                    {COLOR_OPTIONS.map((c) => (
                      <button
                        key={c.name}
                        onClick={() => update("color_code", c.value)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${form.color_code === c.value ? "border-slate-900 shadow-md" : "border-transparent hover:border-slate-300"}`}
                        data-testid={`color-${c.name.toLowerCase()}`}
                      >
                        <div className="h-10 w-10 rounded-lg" style={{ backgroundColor: c.value || "#E2E8F0" }}>
                          {form.color_code === c.value && (
                            <div className="h-full w-full flex items-center justify-center text-white text-xs font-bold">
                              {c.value ? "Sel" : <span className="text-slate-500">Sel</span>}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Disable modifier popup</Label>
                  <Checkbox checked={form.disable_modifier_popup} onCheckedChange={(v) => update("disable_modifier_popup", v)} />
                </div>
                <div className="grid grid-cols-[180px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Printers</Label>
                  <Input value={(form.printers || []).join(", ")} onChange={(e) => update("printers", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="KDS1, KITCHEN1" />
                </div>
              </div>
            </div>
          )}


          {/* === DINING TYPES === */}
          {expanded.dining && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="dining-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900">Dining Types</h2>
                <p className="text-xs text-slate-400">
                  {form.dining_type_ids.length === 0 ? "All types (none restricted)" : `${form.dining_type_ids.length} of ${allDiningTypes.length} types selected`}
                </p>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-slate-500 mb-4">
                  Select which dining/order types this product is available for. <strong>Leave all unchecked</strong> to make it available for all types.
                  Check specific types to <strong>restrict</strong> availability (e.g., alcohol → Dine In only).
                </p>
                {allDiningTypes.filter((dt) => dt.is_active).length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="dining-type-assignment">
                    {allDiningTypes.filter((dt) => dt.is_active).map((dt) => {
                      const isSelected = form.dining_type_ids.includes(dt.dining_type_id);
                      return (
                        <label
                          key={dt.dining_type_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? "border-[var(--brand-primary)] bg-blue-50/40 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
                          data-testid={`dining-assign-${dt.dining_type_id}`}
                        >
                          <Checkbox checked={isSelected} onCheckedChange={() => toggleDiningType(dt.dining_type_id)} />
                          <div>
                            <div className="flex items-center gap-1.5">
                              <UtensilsCrossed className="h-3.5 w-3.5 text-[var(--brand-primary)]" />
                              <span className="text-sm font-medium text-slate-800">{dt.name}</span>
                            </div>
                            {dt.short_name && <span className="text-xs text-slate-400">{dt.short_name}</span>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-6">No dining types configured. Go to Settings &gt; Dining Types to add them.</p>
                )}

                {form.dining_type_ids.length > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-medium text-amber-700">
                      This product is <strong>restricted</strong> to: {form.dining_type_ids.map((id) => allDiningTypes.find((d) => d.dining_type_id === id)?.name).filter(Boolean).join(", ")}.
                      It will <strong>not appear</strong> in the POS for other order types.
                    </p>
                  </div>
                )}
                {form.dining_type_ids.length === 0 && allDiningTypes.length > 0 && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-xs font-medium text-emerald-700">
                      No restrictions — this product is available for <strong>all</strong> dining types.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* === SIZES & PRICING === */}
          {expanded.sizes && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="sizes-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900">Sizes & Pricing</h2>
                <p className="text-xs text-slate-400">{form.size_prices.filter((sp) => sp.price > 0).length} sizes with pricing</p>
              </div>
              <div className="px-6 py-4">
                {/* Size Group Selector */}
                <div className="grid grid-cols-[180px_1fr] items-center gap-4 mb-4">
                  <Label className="text-sm text-slate-600 text-right">Size Group</Label>
                  <Select value={form.size_group_id || "none"} onValueChange={(v) => {
                    update("size_group_id", v === "none" ? "" : v);
                    if (v === "none") update("size_prices", []);
                  }}>
                    <SelectTrigger className="max-w-sm" data-testid="product-size-group-select"><SelectValue placeholder="None (no sizes)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (no sizes)</SelectItem>
                      {allSizeGroups.filter((g) => g.is_active).map((g) => (
                        <SelectItem key={g.size_group_id} value={g.size_group_id}>{g.name} ({g.sizes?.length || 0} sizes)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Size pricing grid - only for selected group */}
                {form.size_group_id && (() => {
                  const selectedGroup = allSizeGroups.find((g) => g.size_group_id === form.size_group_id);
                  const groupSizes = (selectedGroup?.sizes || []).filter((s) => s.is_active);
                  if (groupSizes.length === 0) return <p className="text-sm text-slate-400 text-center py-6">No active sizes in this group.</p>;
                  return (
                    <>
                      <p className="text-sm text-slate-500 mb-3">
                        Set the base price for each size. The base "Price" above is the default if no size is picked.
                      </p>
                      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="size-pricing-grid">
                        <div className="grid grid-cols-[minmax(0,1fr)_80px_120px] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                          <div>Size</div>
                          <div className="text-center">Short</div>
                          <div className="text-right">Price ($)</div>
                        </div>
                        {groupSizes.map((size) => {
                          const existing = form.size_prices.find((sp) => sp.size_id === size.size_id);
                          const currentPrice = existing?.price ?? "";
                          return (
                            <div key={size.size_id} className={`grid grid-cols-[minmax(0,1fr)_80px_120px] gap-2 items-center px-4 py-2.5 border-b border-slate-50 ${currentPrice ? "bg-blue-50/30" : ""}`} data-testid={`size-price-${size.size_id}`}>
                              <div className="flex items-center gap-2">
                                <Ruler className="h-4 w-4 text-[var(--brand-primary)]" />
                                <span className="text-sm font-medium text-slate-800">{size.name}</span>
                              </div>
                              <div className="text-center">
                                <Badge variant="outline" className="text-xs font-mono">{size.short_name}</Badge>
                              </div>
                              <div className="text-right">
                                <Input
                                  type="number" step="0.01" placeholder="0.00"
                                  className="h-8 text-right text-sm max-w-[100px] ml-auto"
                                  value={currentPrice}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setForm((prev) => {
                                      const newPrices = prev.size_prices.filter((sp) => sp.size_id !== size.size_id);
                                      if (val && parseFloat(val) > 0) newPrices.push({ size_id: size.size_id, price: parseFloat(val) });
                                      return { ...prev, size_prices: newPrices };
                                    });
                                  }}
                                  data-testid={`size-price-input-${size.size_id}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {form.size_prices.length > 0 && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="size-pricing-summary">
                          <p className="text-xs font-semibold text-blue-800 mb-1">Configured Sizes:</p>
                          <div className="flex flex-wrap gap-2">
                            {form.size_prices.filter((sp) => sp.price > 0).map((sp) => {
                              const size = allSizes.find((s) => s.size_id === sp.size_id);
                              return <Badge key={sp.size_id} variant="outline" className="text-xs text-blue-700 border-blue-300 bg-white">{size?.name || sp.size_id}: ${sp.price.toFixed(2)}</Badge>;
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Size Included Items */}
                {form.size_group_id && activeSizes.length > 0 && (
                  <div className="mt-4 border-t border-slate-200 pt-4" data-testid="size-included-items">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Included with Size</h3>
                    <p className="text-xs text-slate-400 mb-3">Configure items that come included with each size (e.g., Large includes a breadstick).</p>
                    {activeSizes.map((size) => {
                      const items = form.size_included_items?.[size.size_id] || [];
                      return (
                        <div key={size.size_id} className="mb-3 bg-slate-50 rounded-lg p-3 border border-slate-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-slate-700">{size.name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const newItems = [...items, { name: "", quantity: 1 }];
                                setForm((prev) => ({ ...prev, size_included_items: { ...prev.size_included_items, [size.size_id]: newItems } }));
                              }}
                              className="text-[10px] text-blue-600 hover:underline font-medium"
                              data-testid={`add-include-${size.size_id}`}
                            >+ Add Item</button>
                          </div>
                          {items.length === 0 && <p className="text-[10px] text-slate-400 italic">No included items</p>}
                          {items.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 mb-1">
                              <Input className="h-7 text-xs flex-1" placeholder="Item name" value={item.name}
                                onChange={(e) => {
                                  const updated = [...items];
                                  updated[i] = { ...updated[i], name: e.target.value };
                                  setForm((prev) => ({ ...prev, size_included_items: { ...prev.size_included_items, [size.size_id]: updated } }));
                                }}
                                data-testid={`include-name-${size.size_id}-${i}`} />
                              <Input type="number" min="1" className="h-7 text-xs w-14 text-center" placeholder="Qty" value={item.quantity}
                                onChange={(e) => {
                                  const updated = [...items];
                                  updated[i] = { ...updated[i], quantity: parseInt(e.target.value) || 1 };
                                  setForm((prev) => ({ ...prev, size_included_items: { ...prev.size_included_items, [size.size_id]: updated } }));
                                }}
                                data-testid={`include-qty-${size.size_id}-${i}`} />
                              <button type="button" className="text-red-400 hover:text-red-600 p-1"
                                onClick={() => {
                                  const updated = items.filter((_, j) => j !== i);
                                  setForm((prev) => ({ ...prev, size_included_items: { ...prev.size_included_items, [size.size_id]: updated } }));
                                }}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {!form.size_group_id && (
                  <p className="text-sm text-slate-400 text-center py-6">
                    No size group selected. This product has a single price. Select a group above to enable size-based pricing.
                  </p>
                )}
              </div>
            </div>
          )}


          {/* === TAX OPTIONS === */}
          {expanded.taxes && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="taxes-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900">Tax Options</h2>
                <p className="text-xs text-slate-400">
                  {form.tax_ids.length} of {allTaxRates.length} taxes applied
                  {form.tax_ids.length > 0 && ` | Combined: ${allTaxRates.filter((t) => form.tax_ids.includes(t.tax_id)).reduce((s, t) => s + t.rate, 0).toFixed(2)}%`}
                </p>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-slate-500 mb-4">
                  Select which taxes apply to this product. Default taxes are pre-checked for new products.
                </p>
                {allTaxRates.length > 0 ? (
                  <div className="space-y-2" data-testid="tax-assignment-list">
                    {allTaxRates.filter((t) => t.is_active).map((tax) => {
                      const isApplied = form.tax_ids.includes(tax.tax_id);
                      return (
                        <label
                          key={tax.tax_id}
                          className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-colors ${isApplied ? "border-[var(--brand-primary)] bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}
                          data-testid={`tax-assign-${tax.tax_id}`}
                        >
                          <Checkbox checked={isApplied} onCheckedChange={() => toggleTaxId(tax.tax_id)} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Percent className="h-4 w-4 text-[var(--brand-primary)]" />
                              <span className="text-sm font-medium text-slate-800">{tax.name}</span>
                              {tax.is_default && <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">Default</Badge>}
                            </div>
                          </div>
                          <span className="text-sm font-mono font-semibold text-slate-700">{tax.rate.toFixed(2)}%</span>
                          <Badge variant="outline" className="text-xs capitalize">{tax.tax_type}</Badge>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No tax rates configured. Go to Settings &gt; Tax Settings to add them.</p>
                )}

                {form.tax_ids.length > 0 && parseFloat(form.price) > 0 && (
                  <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="tax-preview">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Tax Preview for ${parseFloat(form.price).toFixed(2)}</p>
                    {allTaxRates.filter((t) => form.tax_ids.includes(t.tax_id)).map((tax) => (
                      <div key={tax.tax_id} className="flex justify-between text-sm">
                        <span className="text-slate-500">{tax.name} ({tax.rate}%)</span>
                        <span className="font-medium">${(parseFloat(form.price) * tax.rate / 100).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-1 mt-1">
                      <span>Total with tax</span>
                      <span>${(parseFloat(form.price) * (1 + allTaxRates.filter((t) => form.tax_ids.includes(t.tax_id)).reduce((s, t) => s + t.rate, 0) / 100)).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === MODIFIERS === */}
          {expanded.modifiers && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="modifiers-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900">Modifiers</h2>
                <p className="text-xs text-slate-400">{form.modifier_group_ids.length} groups assigned | {form.default_modifier_ids.length} defaults set</p>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-slate-500 mb-4">
                  Select modifier groups to assign. Mark modifiers as <Star className="h-3.5 w-3.5 inline text-amber-500" /> <strong>Default</strong> if they come on the product.
                  {activeSizes.length > 0 && " Size price columns let you set what each modifier costs per size."}
                </p>

                <div className="space-y-3" data-testid="modifier-groups-assignment">
                  {/* Assigned modifier groups - draggable for ordering */}
                  {form.modifier_group_ids.length > 0 && (
                    <div className="space-y-1 mb-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                        <GripVertical className="h-3 w-3" /> Assigned (drag to reorder POS question order)
                      </p>
                      <DndContext sensors={pdpSensors} collisionDetection={closestCenter} onDragEnd={handleModGroupDragEnd}>
                        <SortableContext items={form.modifier_group_ids} strategy={verticalListSortingStrategy}>
                          {form.modifier_group_ids.map((gid, idx) => {
                            const group = allModGroups.find((g) => g.modifier_group_id === gid);
                            if (!group) return null;
                            return <SortableModGroupRow key={gid} group={group} idx={idx} form={form} toggleModGroup={toggleModGroup} activeSizes={activeSizes} updateOverride={updateGroupOverride} updateModOverride={updateModifierOverride} />;
                          })}
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}

                  {/* Unassigned modifier groups - simple toggle */}
                  {allModGroups.filter((g) => !form.modifier_group_ids.includes(g.modifier_group_id)).length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Unassigned Groups</p>
                      {allModGroups.filter((g) => !form.modifier_group_ids.includes(g.modifier_group_id)).map((group) => (
                        <div key={group.modifier_group_id} className="border rounded-lg overflow-hidden border-slate-200">
                          <div className="flex items-center gap-3 px-4 py-3">
                            <Checkbox
                              checked={false}
                              onCheckedChange={() => toggleModGroup(group.modifier_group_id)}
                              data-testid={`assign-group-${group.modifier_group_id}`}
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-slate-500">{group.name}</span>
                              <span className="text-xs text-slate-400 ml-2">
                                (Min: {group.min_select}, Max: {group.max_select}, Free: {group.free_amount})
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {allModGroups.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-8">No modifier groups created yet. Create them in the Modifiers section first.</p>
                  )}
                </div>

                {form.default_modifier_ids.length > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-medium text-amber-700 mb-1">Default Modifiers (come on the product):</p>
                    <div className="flex flex-wrap gap-1">
                      {form.default_modifier_ids.map((modId) => {
                        const mod = allModGroups.flatMap((g) => g.modifiers || []).find((m) => m.modifier_id === modId);
                        return mod ? (
                          <Badge key={modId} variant="outline" className="text-xs text-amber-700 border-amber-300">
                            <Star className="h-3 w-3 mr-0.5 fill-amber-500" />{mod.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                    <p className="text-xs text-amber-600 mt-2">
                      Customer can uncheck defaults (no price change, prints as "NO [item]"). Adding non-default modifiers charges their price.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === COMBO MEAL === */}
          {expanded.combo && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="combo-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Package className="h-4 w-4 text-[var(--brand-primary)]" /> Combo Meal
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">Enable Combo</span>
                  <Switch checked={form.is_combo} onCheckedChange={(v) => update("is_combo", v)} data-testid="combo-toggle" />
                </div>
              </div>
              <div className="px-6 py-4">
                {!form.is_combo ? (
                  <p className="text-sm text-slate-400 text-center py-6">
                    Enable the combo toggle above to configure this product as a combo meal with selectable slots.
                  </p>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">
                      Define combo slots (e.g., "Choose Your Drink", "Choose Your Side"). Each slot specifies how many items the customer picks and which products are eligible.
                      The combo is priced at its own base price — modifier upcharges from individual items still apply on top.
                    </p>

                    {form.combo_slots.map((slot, idx) => (
                      <div key={slot.slot_id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`combo-slot-${slot.slot_id}`}>
                        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <span className="text-xs font-bold text-slate-400 uppercase">Slot {idx + 1}</span>
                          <Input
                            value={slot.slot_name}
                            onChange={(e) => updateComboSlot(slot.slot_id, "slot_name", e.target.value)}
                            className="h-8 text-sm font-medium max-w-[200px]"
                            placeholder="Slot name..."
                            data-testid={`combo-slot-name-${slot.slot_id}`}
                          />
                          <div className="flex items-center gap-2 ml-auto">
                            <Label className="text-xs text-slate-500">Qty:</Label>
                            <Input
                              type="number" min="1" max="10"
                              value={slot.quantity}
                              onChange={(e) => updateComboSlot(slot.slot_id, "quantity", parseInt(e.target.value) || 1)}
                              className="h-8 w-16 text-sm text-center"
                              data-testid={`combo-slot-qty-${slot.slot_id}`}
                            />
                            <button
                              onClick={() => removeComboSlot(slot.slot_id)}
                              className="h-8 w-8 rounded bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors"
                              data-testid={`combo-slot-remove-${slot.slot_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Eligible Products</p>
                            <Badge variant="outline" className="text-xs">{slot.eligible_product_ids.length} selected</Badge>
                          </div>
                          {/* Product picker */}
                          <div className="max-h-[200px] overflow-y-auto space-y-1 border border-slate-100 rounded-lg p-2">
                            {allProducts.filter((p) => p.product_id !== productId && !p.is_combo).length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-4">No eligible products found</p>
                            ) : (
                              allProducts.filter((p) => p.product_id !== productId && !p.is_combo).map((p) => {
                                const isEligible = slot.eligible_product_ids.includes(p.product_id);
                                return (
                                  <label
                                    key={p.product_id}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${isEligible ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50 border border-transparent"}`}
                                    data-testid={`combo-eligible-${slot.slot_id}-${p.product_id}`}
                                  >
                                    <Checkbox checked={isEligible} onCheckedChange={() => toggleSlotProduct(slot.slot_id, p.product_id)} />
                                    <span className="text-sm text-slate-800 flex-1 truncate">{p.name}</span>
                                    <span className="text-xs text-slate-400">{categories.find((c) => c.category_id === p.category_id)?.name || ""}</span>
                                    <span className="text-xs font-mono text-slate-500">${(p.price || 0).toFixed(2)}</span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          {slot.eligible_product_ids.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {slot.eligible_product_ids.map((pid) => {
                                const prod = allProducts.find((p) => p.product_id === pid);
                                return prod ? (
                                  <Badge key={pid} variant="outline" className="text-xs text-blue-700 border-blue-300 bg-blue-50">
                                    {prod.name}
                                    <button onClick={() => toggleSlotProduct(slot.slot_id, pid)} className="ml-1 hover:text-red-500">x</button>
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    <Button variant="outline" onClick={addComboSlot} className="w-full border-dashed" data-testid="add-combo-slot-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add Combo Slot
                    </Button>

                    {form.combo_slots.length > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs font-semibold text-blue-800 mb-1">Combo Summary:</p>
                        <p className="text-xs text-blue-700">
                          {form.combo_slots.length} slot{form.combo_slots.length !== 1 ? "s" : ""} configured.
                          Customer picks {form.combo_slots.map((s) => `${s.quantity} from "${s.slot_name}"`).join(", ")}.
                          Base combo price: ${parseFloat(form.price || 0).toFixed(2)} + modifier upcharges.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === RECIPE / INGREDIENTS === */}
          {expanded.recipe && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="recipe-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
                  <FlaskConical className="h-4 w-4 text-[var(--brand-primary)]" /> Recipe / Ingredients
                </h2>
                <Badge variant="outline" className="text-xs">{form.recipe_ingredients.length} ingredient{form.recipe_ingredients.length !== 1 ? "s" : ""}</Badge>
              </div>
              <div className="px-6 py-4 space-y-4">
                <p className="text-sm text-slate-500">
                  Define which ingredients are used to make this product. When sold, these quantities are automatically deducted from inventory.
                </p>

                <div className="flex items-center gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Checkbox checked={form.allow_negative_stock} onCheckedChange={(v) => update("allow_negative_stock", v)} data-testid="allow-negative-stock" />
                    <Label className="text-sm font-medium text-slate-700">Allow sales even if ingredients are out of stock</Label>
                  </div>
                  <p className="text-xs text-slate-400 flex-1">When unchecked, POS will block this item when any recipe ingredient hits zero.</p>
                </div>

                {form.recipe_ingredients.map((ri, idx) => {
                  const ing = allIngredients.find((i) => i.ingredient_id === ri.ingredient_id);
                  return (
                    <div key={idx} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-white" data-testid={`recipe-ing-${idx}`}>
                      <FlaskConical className="h-4 w-4 text-[var(--brand-primary)] flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-800 min-w-[150px] truncate">{ing?.name || ri.ingredient_id}</span>
                      <Input
                        type="number" step="0.1" min="0"
                        className="h-8 w-20 text-sm"
                        value={ri.qty}
                        onChange={(e) => {
                          const updated = [...form.recipe_ingredients];
                          updated[idx] = { ...updated[idx], qty: parseFloat(e.target.value) || 0 };
                          update("recipe_ingredients", updated);
                        }}
                        data-testid={`recipe-qty-${idx}`}
                      />
                      <Select value={ri.unit || ing?.stock_unit || "each"} onValueChange={(v) => {
                        const updated = [...form.recipe_ingredients];
                        updated[idx] = { ...updated[idx], unit: v };
                        update("recipe_ingredients", updated);
                      }}>
                        <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["each","dozen","oz","lb","g","kg","ml","l","fl_oz","cup","gallon","case"].map((u) => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {ing && <span className="text-xs text-slate-400">Stock: {(ing.current_stock || 0).toFixed(1)} {ing.stock_unit}</span>}
                      <button
                        onClick={() => update("recipe_ingredients", form.recipe_ingredients.filter((_, i) => i !== idx))}
                        className="h-7 w-7 rounded bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 ml-auto"
                        data-testid={`recipe-remove-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}

                <Select
                  value=""
                  onValueChange={(ingId) => {
                    const ing = allIngredients.find((i) => i.ingredient_id === ingId);
                    if (!ing) return;
                    if (form.recipe_ingredients.some((r) => r.ingredient_id === ingId)) { toast.error("Already added"); return; }
                    update("recipe_ingredients", [...form.recipe_ingredients, { ingredient_id: ingId, qty: 1, unit: ing.stock_unit }]);
                  }}
                >
                  <SelectTrigger className="border-dashed" data-testid="add-recipe-ingredient-select">
                    <SelectValue placeholder="+ Add ingredient to recipe..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allIngredients.filter((i) => !form.recipe_ingredients.some((r) => r.ingredient_id === i.ingredient_id)).map((i) => (
                      <SelectItem key={i.ingredient_id} value={i.ingredient_id}>{i.name} ({(i.current_stock || 0).toFixed(1)} {i.stock_unit})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {form.recipe_ingredients.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs font-semibold text-blue-800 mb-1">Recipe Cost Estimate:</p>
                    <p className="text-xs text-blue-700">
                      {form.recipe_ingredients.map((ri) => {
                        const ing = allIngredients.find((i) => i.ingredient_id === ri.ingredient_id);
                        return ing ? `${ri.qty} ${ri.unit} ${ing.name}` : null;
                      }).filter(Boolean).join(" + ")}
                      {" = $"}
                      {form.recipe_ingredients.reduce((sum, ri) => {
                        const ing = allIngredients.find((i) => i.ingredient_id === ri.ingredient_id);
                        return sum + (ing ? ri.qty * (ing.cost_per_unit || 0) : 0);
                      }, 0).toFixed(2)} est. cost
                    </p>
                  </div>
                )}

                {allIngredients.length === 0 && (
                  <div className="text-center py-6 text-slate-400 text-sm">
                    No ingredients created yet. Go to <strong>Inventory</strong> to add ingredients first.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
